import {
  GET_WHATSAPP_CONVERSATIONS,
  GET_WHATSAPP_CHAT_MESSAGES,
  SEND_WHATSAPP_REPLY,
  SET_SELECTED_WA_CONVERSATION,
  SET_WA_CONVERSATIONS_LOADING,
  SET_WA_MESSAGES_LOADING,
  PREPEND_WHATSAPP_CHAT_MESSAGES,
  MERGE_WHATSAPP_CHAT_MESSAGES,
  SET_WA_LOADING_MORE,
  SET_WA_HAS_MORE,
  CLEAR_WA_CHAT_UNREAD,
} from '../../shared/constants/ActionTypes';
import {
  mergeMessagePage,
  appendOwnMessage,
  applyLocalReads,
  readMarkFor,
} from './messageMerge';

const initialState = {
  conversations: [],
  selectedChat: null,
  messages: [],
  loadingConversations: false,
  loadingMessages: false,
  loadingMore: false,
  hasMore: false,
  sendingReply: false,
  // chatId -> the newest message time when the user last read it. Not shown;
  // it is what stops a refresh resurrecting a badge that was just cleared.
  readAt: new Map(),
};

const Conversations = (state = initialState, action) => {
  switch (action.type) {
    case SET_WA_CONVERSATIONS_LOADING:
      return {...state, loadingConversations: !!action.payload};

    case GET_WHATSAPP_CONVERSATIONS:
      return {
        ...state,
        // WhatsApp reports a chat unread for a while after it was read, so the
        // server's count is re-applied over the local clear unless something
        // newer has actually arrived.
        conversations: applyLocalReads(action.payload || [], state.readAt),
        loadingConversations: false,
      };

    case SET_SELECTED_WA_CONVERSATION:
      return {
        ...state,
        selectedChat: action.payload,
        messages: [],
        hasMore: false,
        loadingMore: false,
      };

    case SET_WA_MESSAGES_LOADING:
      return {...state, loadingMessages: !!action.payload};

    case GET_WHATSAPP_CHAT_MESSAGES: {
      // Discard results that arrived for a chat that is no longer selected
      // (can happen when the user switches chats quickly or the 5s poll fires
      // after the user has already moved to a different conversation).
      const currentId = state.selectedChat?.chatId;
      const incomingId = action.payload?.chatId;
      if (incomingId && currentId && incomingId !== currentId) {
        return {...state, loadingMessages: false};
      }
      return {
        ...state,
        messages: action.payload?.messages || [],
        hasMore: !!action.payload?.hasMore,
        loadingMessages: false,
      };
    }

    case MERGE_WHATSAPP_CHAT_MESSAGES: {
      // Same stale-chat guard as the full fetch: a poll that lands after the
      // user has moved on must not write into the thread they are now reading.
      const pollId = state.selectedChat?.chatId;
      const forId = action.payload?.chatId;
      if (forId && pollId && forId !== pollId) return state;
      return {
        ...state,
        messages: mergeMessagePage(state.messages, action.payload?.messages),
      };
    }

    case PREPEND_WHATSAPP_CHAT_MESSAGES: {
      const currentId2 = state.selectedChat?.chatId;
      const incomingId2 = action.payload?.chatId;
      if (incomingId2 && currentId2 && incomingId2 !== currentId2) {
        return {...state, loadingMore: false};
      }
      return {
        ...state,
        messages: [...(action.payload?.messages || []), ...state.messages],
        hasMore: !!action.payload?.hasMore,
        loadingMore: false,
      };
    }

    case SET_WA_LOADING_MORE:
      return {...state, loadingMore: !!action.payload};

    case SET_WA_HAS_MORE:
      return {...state, hasMore: !!action.payload};

    case CLEAR_WA_CHAT_UNREAD: {
      const opened = state.conversations.find(
        (c) => c.chatId === action.payload,
      );
      const readAt = new Map(state.readAt);
      readAt.set(action.payload, readMarkFor(opened));
      return {
        ...state,
        readAt,
        conversations: state.conversations.map((c) =>
          c.chatId === action.payload ? {...c, unreadCount: 0} : c,
        ),
      };
    }

    case SEND_WHATSAPP_REPLY:
      return {
        ...state,
        messages: appendOwnMessage(state.messages, action.payload),
      };

    default:
      return state;
  }
};

export default Conversations;
